import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { ItandiChip } from "@itandi/itandi-bb-ui/ItandiChip";
import { useRouter } from "next/router";
import type { ReactNode } from "react";
import React from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { create, update } from "~/hooks/vendors/useSurveySettings";
import type { NskResponse } from "~/models/Api";
import type { SurveyOptionSetting, SurveySetting } from "~/models/Survey";
import {
	buildInitialSurveySetting,
	MAX_NUMBER_OF_OPTIONS,
	validationSurveySchema
} from "~/models/Survey";
import { PATH as INDEX_PATH } from "~/pages/vendors/settings/surveys/index";
import { isSuccess } from "~/utils/api/api_handler";
import Styles from "./SurveyForm.module.scss";
import { SurveyOptionSettingForm } from "./SurveyOptionForm";

type Props = {
	handleMutate?: (() => Promise<void>) | null;
	survey?: SurveySetting | null;
};

export const SurveyForm: React.FC<Props> = ({
	survey = null,
	handleMutate = null
}: Props) => {
	const methods = useForm<SurveySetting>({
		defaultValues: survey || buildInitialSurveySetting,
		mode: "onBlur",
		resolver: yupResolver(validationSurveySchema)
	});
	const { errors, isSubmitting } = methods.formState;
	const { fields, append, remove } = useFieldArray({
		control: methods.control,
		name: "surveyOptions"
	});
	const router = useRouter();
	const addOption = React.useCallback((): void => {
		if (methods.getValues().surveyOptions.length >= MAX_NUMBER_OF_OPTIONS) {
			// 最大数以上は生成しない
			return;
		}
		append({ id: null, seq: null, text: "", value: 0 });
	}, [append, methods]);
	const removeOption = React.useCallback(
		(index: number): void => {
			remove(index);
		},
		[remove]
	);
	const handleCreate = React.useCallback(
		async (surveySetting: SurveySetting): Promise<NskResponse> =>
			create(surveySetting),
		[]
	);
	const handleUpdate = React.useCallback(
		async (
			surveySetting: SurveySetting,
			surveyId: number
		): Promise<NskResponse> => update(surveySetting, surveyId),
		[]
	);
	const handleSubmit = React.useCallback(() => {
		methods.handleSubmit(async () => {
			let result: NskResponse;
			if (survey === null) {
				result = await handleCreate(methods.getValues());
			} else {
				if (survey.id === null || handleMutate === null) return;
				result = await handleUpdate(methods.getValues(), survey.id);
				await handleMutate();
			}
			if (isSuccess(result)) {
				await router.push(INDEX_PATH);
				toast.success("保存しました");
				return;
			}
			toast.error(`エラーが発生しました。${result.message}`);
		})();
	}, [handleCreate, handleMutate, handleUpdate, methods, router, survey]);
	const renderSurveyOption = React.useCallback(
		(
			surveyOption: SurveyOptionSetting,
			surveyOptionIndex: number
		): ReactNode => (
			<SurveyOptionSettingForm
				deleteOption={removeOption}
				key={surveyOption.id}
				surveyOptionIndex={surveyOptionIndex}
			/>
		),
		[removeOption]
	);
	return (
		<FormProvider {...methods}>
			<div className={Styles.Forms}>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabelCol}>
						設問タイトル
						<span className={Styles.RequiredLabel}>
							<ItandiChip label="必須" />
						</span>
					</div>
					<div className={Styles.FormSettingCol}>
						<div>
							<Input
								isInvalid={!!errors.title}
								{...methods.register("title")}
							/>
						</div>
						{errors.title ? (
							<div className={Styles.InvalidFeedback}>
								{errors.title.message}
							</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabelCol}>設問説明文</div>
					<div className={Styles.FormSettingCol}>
						<div>
							<Input
								isInvalid={!!errors.description}
								{...methods.register("description")}
							/>
						</div>
						{errors.description ? (
							<div className={Styles.InvalidFeedback}>
								{errors.description.message}
							</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabelCol}>備考入力</div>
					<div className={Styles.FormSettingCol}>
						<div>
							<InputCheckBox
								checked={methods.watch("enabledNote")}
								label="備考入力を使用する"
								onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
									methods.setValue("enabledNote", e.target.checked);
								}}
							/>
						</div>
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabelCol}>
						選択肢
						<span className={Styles.RequiredLabel}>
							<ItandiChip label="必須" />
						</span>
					</div>
					<div className={Styles.FormSettingCol}>
						<div className={Styles.RadioGroup}>
							{fields.map(renderSurveyOption)}
						</div>
						{methods.getValues().surveyOptions.length <
							MAX_NUMBER_OF_OPTIONS && (
							<Button height="small" onClick={addOption}>
								選択肢を追加する
							</Button>
						)}
						{errors.surveyOptions ? (
							<div className={Styles.InvalidFeedback}>
								{errors.surveyOptions.message}
							</div>
						) : null}
					</div>
				</div>
			</div>
			<div className={Styles.Buttons}>
				<Button
					disabled={isSubmitting}
					height="default"
					onClick={handleSubmit}
					variant="secondary"
				>
					{survey === null ? "アンケートを作成" : "アンケートを更新"}
				</Button>
			</div>
		</FormProvider>
	);
};
